import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Check, Loader2, Sparkles, Bot, ListTodo, MessageCircle, Workflow, ChevronRight } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta, eur } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { BillingDialog } from "@/components/app/BillingDialog";

const RETURN_URL = "/app/billing";
const CREDIT_ORDER = ["credits_10", "credits_50", "credits_200", "credits_500"];
const TIER_META = {
  plus: { nameKey: "tier.plus", blurbKey: "tier.plus_blurb" },
  pro: { nameKey: "tier.pro", blurbKey: "tier.pro_blurb" },
  unlimited: { nameKey: "tier.unlimited", blurbKey: "tier.unlimited_blurb" },
  coach_pro: { nameKey: "tier.coach_pro", blurbKey: "tier.coach_pro_blurb" },
};
const baseOf = (tier) => (tier || "free").replace("_yearly", "");

function tierFeatures(tr, t) {
  const lines = [];
  if (tr.reports_quota >= 9999) lines.push(t("feat.reports_unlimited"));
  else lines.push(t("feat.reports_count", { n: tr.reports_quota }));
  if (!tr.ads) lines.push(t("feat.ad_free"));
  if (tr.voice_journal) lines.push(t("feat.voice_journal"));
  if (tr.starter_plans) lines.push(t("feat.starter_plans"));
  if (tr.priority) lines.push(t("feat.priority"));
  if (tr.coach_commission_pct != null) lines.push(t("feat.commission", { pct: Math.round(tr.coach_commission_pct * 100) }));
  if (tr.coach_advanced_analytics) lines.push(t("feat.advanced_analytics"));
  return lines;
}

export default function Billing() {
  const t = useT();
  usePageMeta(t("bill.title"));
  const { user, refreshUser } = useAuth();
  const isCoach = user?.role === "coach";

  // Credits
  const [packages, setPackages] = useState(null);
  const [buying, setBuying] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [pendingPkg, setPendingPkg] = useState(null);

  // Membership
  const [tiers, setTiers] = useState(null);
  const [mine, setMine] = useState(null);
  const [interval, setIntervalState] = useState("month");
  const [subBusy, setSubBusy] = useState(null);
  const [managing, setManaging] = useState(false);

  // AI assistant
  const [aiStatus, setAiStatus] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get("/credits/packages").then((r) => setPackages(r.data || {})).catch(() => setPackages({}));
    api.get("/subscription/tiers").then((r) => setTiers(r.data || {})).catch(() => setTiers({}));
    api.get("/subscription/mine").then((r) => setMine(r.data || {})).catch(() => setMine({}));
    if (isCoach) api.get("/coach/assistant/status").then((r) => setAiStatus(r.data)).catch(() => setAiStatus({}));
  }, [isCoach]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const sub = params.get("sub");
    if (checkout === "success") {
      toast.success(t("bill.payment_received"));
      refreshUser().catch(() => {});
      window.history.replaceState(null, "", RETURN_URL);
    } else if (checkout === "cancel") {
      toast(t("bill.cancelled"));
      window.history.replaceState(null, "", RETURN_URL);
    } else if (sub === "success") {
      toast.success(t("bill.sub_activated"));
      refreshUser().catch(() => {});
      api.get("/subscription/mine").then((r) => setMine(r.data || {})).catch(() => {});
      window.history.replaceState(null, "", RETURN_URL);
    } else if (sub === "cancel") {
      toast(t("bill.cancelled"));
      window.history.replaceState(null, "", RETURN_URL);
    }
  }, [refreshUser]);

  const buyCredits = async (pkg) => {
    setBuying(pkg);
    try {
      const { data } = await api.post("/credits/checkout", { package: pkg, origin_url: window.location.origin + RETURN_URL });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(t("bill.credits_added"));
      await refreshUser();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.error === "billing_required") { setPendingPkg(pkg); setBillingOpen(true); }
      else toast.error(errMessage(err, t("bill.purchase_failed")));
    } finally { setBuying(null); }
  };
  const onBillingSaved = () => { setBillingOpen(false); if (pendingPkg) { const p = pendingPkg; setPendingPkg(null); buyCredits(p); } };

  const subscribe = async (tierKey) => {
    setSubBusy(tierKey);
    try {
      const { data } = await api.post("/subscribe/app", { tier: tierKey, payment_mode: "stripe", origin_url: window.location.origin + RETURN_URL });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(t("bill.activated"));
      await refreshUser();
      const r = await api.get("/subscription/mine"); setMine(r.data || {});
    } catch (err) { toast.error(errMessage(err, t("bill.finish_failed"))); }
    finally { setSubBusy(null); }
  };

  const manageSub = async () => {
    setManaging(true);
    try {
      const { data: info } = await api.get("/subscription/cancel-info");
      if (info.source === "stripe") {
        const { data } = await api.post("/subscription/cancel-portal", { return_url: window.location.origin + RETURN_URL });
        if (data?.url && !data.mock) { window.location.href = data.url; return; }
        toast(t("bill.manage_demo"));
      } else if (info.source === "apple") {
        window.open(info.deep_link_ios, "_blank");
      } else if (info.source === "google") {
        window.open(info.deep_link_android, "_blank");
      } else {
        toast(info.message || t("bill.no_active_sub"));
      }
    } catch (err) { toast.error(errMessage(err, t("bill.manage_failed"))); }
    finally { setManaging(false); }
  };

  const subscribeAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/coach/assistant/subscribe", { origin_url: window.location.origin });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      setAiBusy(false);
    } catch (err) { toast.error(errMessage(err, t("bill.ai_checkout_failed"))); setAiBusy(false); }
  };

  const packs = packages ? CREDIT_ORDER.filter((k) => packages[k]).map((k) => ({ key: k, ...packages[k] })) : null;
  const bases = isCoach ? ["plus", "pro", "unlimited", "coach_pro"] : ["plus", "pro", "unlimited"];
  const currentBase = baseOf(mine?.tier);

  return (
    <div className="space-y-12" data-testid="billing-page">
      <div>
        <h1 className="font-display text-3xl text-foreground">{t("bill.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isCoach ? t("bill.sub_coach") : t("bill.sub_client")}
        </p>
      </div>

      {/* Credits */}
      <section className="space-y-5" data-testid="billing-credits">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("bill.credits")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("bill.credit_rate")}</p>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
            <Coins className="h-4 w-4 text-sage-light" />
            <span className="text-sm font-medium">{t("bill.n_credits", { n: user?.credits ?? 0 })}</span>
          </div>
        </div>
        {packs === null ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {packs.map((p) => {
              const best = p.key === "credits_200";
              return (
                <div key={p.key} className={`relative flex flex-col rounded-2xl border bg-card p-6 ${best ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                  {best && <Badge className="absolute -top-2.5 left-6 rounded-full">{t("bill.popular")}</Badge>}
                  <p className="font-display text-4xl font-semibold text-foreground">{p.credits}</p>
                  <p className="text-sm text-muted-foreground">{t("bill.credits_word")}</p>
                  <p className="mt-4 font-display text-2xl text-primary">{eur(p.amount)}</p>
                  <p className="text-xs text-muted-foreground">{t("bill.incl_vat_per", { per: eur(Math.round(p.amount / p.credits)) })}</p>
                  <Button className="mt-5 w-full rounded-full" onClick={() => buyCredits(p.key)} disabled={!!buying} data-testid={`buy-${p.key}`}>
                    {buying === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : t("bill.buy")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Membership */}
      <section className="space-y-5" data-testid="billing-membership">
        <div>
          <h2 className="font-display text-2xl text-foreground">{t("bill.membership")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("bill.membership_sub")}</p>
        </div>
        {currentBase && currentBase !== "free" ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5" data-testid="billing-manage-sub">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t("bill.current_plan", { name: TIER_META[currentBase] ? t(TIER_META[currentBase].nameKey) : currentBase })}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("bill.manage_hint")}</p>
            </div>
            <Button variant="outline" className="shrink-0 rounded-full" disabled={managing} onClick={manageSub} data-testid="billing-manage-sub-btn">
              {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("bill.manage")}
            </Button>
          </div>
        ) : null}
        <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="billing-interval-toggle">
          {[["month", t("bill.monthly")], ["year", t("bill.yearly")]].map(([v, label]) => (
            <button key={v} onClick={() => setIntervalState(v)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${interval === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`billing-interval-${v}`}>{label}</button>
          ))}
        </div>
        {tiers === null ? (
          <div className="grid gap-5 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}</div>
        ) : (
          <div className={`grid gap-5 ${bases.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
            {bases.map((base) => {
              const key = interval === "year" ? `${base}_yearly` : base;
              const tr = tiers[key] || tiers[base];
              if (!tr) return null;
              const meta = TIER_META[base];
              const isCurrent = currentBase === base && currentBase !== "free";
              const highlight = base === "unlimited";
              return (
                <div key={base} className={`relative flex flex-col rounded-2xl border bg-card p-6 ${highlight ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                  {highlight && <Badge className="absolute -top-2.5 left-6 rounded-full"><Sparkles className="mr-1 h-3 w-3" /> {t("bill.recommended")}</Badge>}
                  <h3 className="font-display text-2xl text-foreground">{t(meta.nameKey)}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{t(meta.blurbKey)}</p>
                  <div className="mt-4"><span className="font-display text-3xl font-semibold text-foreground">{eur(tr.price_eur_cents)}</span><span className="text-sm text-muted-foreground"> / {interval === "year" ? t("bill.per_year") : t("bill.per_month")}</span></div>
                  <ul className="mt-5 flex-1 space-y-2.5">{tierFeatures(tr, t).map((f) => (<li key={f} className="flex items-start gap-2 text-sm text-foreground/90"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}</li>))}</ul>
                  <Button className="mt-6 w-full rounded-full" variant={highlight ? "default" : "outline"} disabled={isCurrent || !!subBusy} onClick={() => subscribe(key)} data-testid={`subscribe-${base}`}>
                    {subBusy === key ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? t("bill.current") : t("bill.choose")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{t("bill.stripe_note")}</p>
      </section>

      {/* AI assistant (coach only) */}
      {isCoach && (
        <section className="space-y-5" data-testid="billing-assistant">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("bill.ai_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("bill.ai_sub")}</p>
          </div>
          {aiStatus === null ? (
            <Skeleton className="h-40 rounded-2xl" />
          ) : aiStatus.active ? (
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="billing-assistant-active">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700"><Bot className="h-4 w-4" /> {t("bill.ai_active")}</div>
                <p className="mt-2 text-sm text-foreground">{aiStatus.cancel_at_period_end ? t("bill.ai_runs_until", { date: (aiStatus.current_period_end || "").slice(0, 10) }) : t("bill.ai_is_active")}</p>
              </div>
              <Button asChild className="rounded-full"><Link to="/app/coach/assistant" data-testid="billing-assistant-open">{t("bill.ai_open")} <ChevronRight className="ml-1 h-4 w-4" /></Link></Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6" data-testid="billing-assistant-upsell">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary"><Bot className="h-4 w-4" /> {t("bill.ai_title")}</div>
              <p className="max-w-xl text-sm text-muted-foreground">{t("bill.ai_upsell")}</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {[[Sparkles, t("bill.ai_f_briefing")], [ListTodo, t("bill.ai_f_tasks")], [MessageCircle, t("bill.ai_f_chat")], [Workflow, t("bill.ai_f_workflows")]].map(([Icon, label]) => (
                  <div key={label} className="flex items-center gap-2 text-sm text-foreground"><Icon className="h-4 w-4 text-primary" /> {label}</div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Button className="rounded-full" disabled={aiBusy} onClick={subscribeAi} data-testid="billing-assistant-subscribe">
                  {aiBusy ? t("bill.ai_starting") : (aiStatus.trial_used ? t("bill.ai_start_sub") : t("bill.ai_trial"))}
                </Button>
                <span className="text-sm font-semibold text-foreground">{t("bill.ai_price", { price: aiStatus.price_eur ?? 20 })}</span>
              </div>
            </div>
          )}
        </section>
      )}

      <BillingDialog open={billingOpen} onOpenChange={setBillingOpen} onSaved={onBillingSaved} />
    </div>
  );
}
